import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCart } from "../../../context/CartContext";
import { useLang } from "../../../context/LanguageContext";
import { useSearch } from "../../../context/SearchContext";
import { useActiveOffers } from "../../../hooks/useActiveOffers";
import { SITE } from "../../../components/layout/site";
import BranchPicker from "../../../components/feature/BranchPicker";

export default function Navbar() {
	const { totalItems, openDrawer } = useCart();
	const { lang, setLang, t } = useLang();
	const { open: openSearch } = useSearch();
	const { hasOffers } = useActiveOffers();
	const [open, setOpen] = useState(false);
	const location = useLocation();

	const menuItems = [
		{ to: "/", name: t("nav_home") },
		{ to: "/products", name: t("nav_catalog") },
		{ to: "/novedades", name: t("nav_news") },
		// Solo aparece mientras haya ofertas vigentes
		...(hasOffers ? [{ to: "/ofertas", name: t("nav_offers"), offer: true }] : []),
		{ to: "/servicios", name: t("nav_services") },
		{ to: "/nosotros", name: t("nav_about") },
		{ to: "/agendar", name: t("nav_book") },
		{ to: "/contacto", name: lang === "en" ? "Contact" : "Contacto" },
	] as { to: string; name: string; offer?: boolean }[];

	// Cierra el menu movil al navegar
	useEffect(() => setOpen(false), [location.pathname]);

	useEffect(() => {
		document.body.style.overflow = open ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [open]);

	const linkClass = (offer?: boolean) =>
		({ isActive }: { isActive: boolean }) =>
			`sp-navbar-link${isActive ? " is-active" : ""}${offer ? " is-offer" : ""}`;

	const cartButton = (
		<button type="button" onClick={openDrawer} className="sp-icon-btn" aria-label="Carrito de compras" title="Carrito">
			<i className="ri-shopping-cart-2-fill" />
			{totalItems > 0 && (
				<span className="sp-badge-count">{totalItems > 99 ? "99+" : totalItems}</span>
			)}
		</button>
	);

	const langSwitch = (
		<div className="sp-lang" role="group" aria-label="Idioma">
			{(["es", "en"] as const).map((l) => (
				<button
					key={l}
					type="button"
					className={lang === l ? "is-active" : ""}
					aria-pressed={lang === l}
					onClick={() => setLang(l)}
				>
					{l.toUpperCase()}
				</button>
			))}
		</div>
	);

	return (
		<>
			<header className="sp-navbar">
				<div className="sp-container sp-navbar-inner">
					<Link to="/" aria-label="SpartechRD — Inicio">
						<img
							src={SITE.logo}
							alt="SpartechRD"
							className="sp-navbar-logo"
							width={40}
							height={48}
							fetchPriority="high"
						/>
					</Link>

					<nav className="sp-navbar-links" aria-label="Principal">
						{menuItems.map((item) => (
							<NavLink key={item.to} to={item.to} end className={linkClass(item.offer)}>
								{item.name}
							</NavLink>
						))}
					</nav>

					<div className="sp-navbar-actions">
						<div className="sp-lang-desktop">
							<BranchPicker />
						</div>
						<button type="button" onClick={openSearch} className="sp-icon-btn" aria-label="Buscar productos" title="Buscar">
							<i className="ri-search-line" />
						</button>
						{/* Seguimiento del servicio técnico: antes solo se llegaba con el enlace del WhatsApp. */}
						<Link to="/seguimiento" className="sp-icon-btn" aria-label={t("trk_nav")} title={t("trk_nav")}>
							<i className="ri-radar-line" />
						</Link>
						<div className="sp-lang-desktop">{langSwitch}</div>
						{cartButton}
						<button
							type="button"
							className="sp-icon-btn sp-menu-toggle"
							aria-label="Abrir menú"
							aria-expanded={open}
							onClick={() => setOpen(true)}
						>
							<i className="ri-menu-line" />
						</button>
					</div>
				</div>
			</header>

			{/* Drawer movil */}
			{open && <div className="sp-drawer-backdrop" onClick={() => setOpen(false)} />}
			<aside className={`sp-drawer${open ? " is-open" : ""}`} aria-hidden={!open}>
				<div className="sp-drawer-head">
					<button
						type="button"
						className="sp-icon-btn"
						aria-label="Cerrar menú"
						onClick={() => setOpen(false)}
						tabIndex={open ? 0 : -1}
					>
						<i className="ri-close-line" />
					</button>
				</div>
				<div style={{ padding: "1rem 14px 0" }}>{langSwitch}</div>
				<BranchPicker variant="list" />
				<nav className="sp-drawer-links" aria-label="Menú móvil">
					{menuItems.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							end
							className={linkClass(item.offer)}
							tabIndex={open ? 0 : -1}
						>
							{item.name}
						</NavLink>
					))}
					<NavLink to="/seguimiento" className={linkClass()} tabIndex={open ? 0 : -1}>
						<i className="ri-radar-line" style={{ marginRight: 8 }} />
						{t("trk_nav")}
					</NavLink>
				</nav>
			</aside>
		</>
	);
}
